import 'intersection-observer';
import * as el from './elements';
import { projects, view } from './view';

class Application {
  #partObserver;
  #fullObserver;

  constructor() {
    this.#infoText();
    this.#hideItems();
    this.#observeElements();
    this.#observeHeader();
    this.#observeSections();

    el.works.addEventListener('click', this.#handleLearnMore);
    el.projectOverlay.addEventListener('click', this.#removeProject);
    el.project.addEventListener('click', this.#handleSlider.bind(this));
    el.hamburger.addEventListener('click', this.#handleHamburger);
    el.navContainer.addEventListener('click', this.#removeNavbar);
    window.addEventListener('keydown', this.#handleEscape.bind(this));
    [el.btnAbout, ...el.navLink].forEach(link =>
      link.addEventListener('click', this.#scrolling)
    );
  }

  #scrolling(e) {
    e.preventDefault();
    const element = document.querySelector(this.getAttribute('href'));
    const elementTop = element.getBoundingClientRect().top;
    window.scroll({
      top: window.pageYOffset + elementTop - 72,
      behavior: 'smooth',
    });
  }

  #removeNavbar(e) {
    const link = e.target.closest('.nav-link');
    if (!link) return;

    el.navbar.classList.remove('show-navbar');
  }

  #handleHamburger() {
    el.navbar.classList.toggle('show-navbar');
  }

  #handleSlider(e) {
    const btnPrev = e.target.closest('.btn-prev');
    const btnNext = e.target.closest('.btn-next');
    const btnClose = e.target.closest('.btn-close-modal');
    if (!btnPrev && !btnNext && !btnClose) return;

    if (btnClose) {
      this.#removeProject();
    }

    const images = el.project.querySelectorAll('.project-image');
    const transforms = Array.from(images).map(img => +img.dataset.id);
    const minTransform = transforms.reduce((prev, curr) =>
      prev > curr ? curr : prev
    );
    const maxTransform = transforms.reduce((prev, curr) =>
      prev > curr ? prev : curr
    );

    if (btnPrev && minTransform !== 0) {
      images.forEach(img => {
        img.dataset.id = +img.dataset.id + 100;
        img.style.transform = `translateX(${img.dataset.id}%)`;
      });
    }

    if (btnNext && maxTransform !== 0) {
      images.forEach(img => {
        img.dataset.id = +img.dataset.id - 100;
        img.style.transform = `translateX(${img.dataset.id}%)`;
      });
    }
  }

  #handleEscape(e) {
    if (e.key === 'Escape') {
      this.#removeProject();
    }
  }

  #handleLearnMore(e) {
    const btnLearn = e.target.closest('.btn-learn');
    if (!btnLearn) return;

    const card = e.target.closest('.card');
    const projectName = card.dataset.name;
    const project = projects.find(project => project.name === projectName);
    view.generateProject(project);

    el.projectOverlay.classList.remove('hidden');
    el.project.classList.remove('hidden');
  }

  #infoText() {
    for (let i = 0; i < 4; i++) {
      el.infoItems.insertAdjacentElement(
        'afterbegin',
        el.infoItem.cloneNode(true)
      );
    }
  }

  #removeProject() {
    el.project.classList.add('hidden');
    el.projectOverlay.classList.add('hidden');
    el.project.innerHTML = '';
  }

  #hideItems() {
    [
      ...el.titleDark,
      el.description,
      el.navContainer,
      el.me,
      el.info,
      el.progress,
      el.cubeWrapper,
    ].forEach(item => item.classList.add('hidden'));

    if (window.innerWidth > 663) {
      el.works.classList.add('hidden');
    } else {
      el.projectCollection.forEach(item => item.classList.add('hidden'));
    }
  }

  #showSingleItem(item, animation) {
    item.classList.remove('hidden');
    item.classList.add(animation);
  }

  #showCollection(collection, step, animation, hide = true) {
    if (hide) {
      collection.forEach(item => item.classList.add('hide-item'));
    }

    let index = 0;
    let intervarID;
    intervarID = setInterval(function () {
      collection[index].classList.add(animation);
      index++;

      if (index === collection.length) clearInterval(intervarID);
    }, step);
  }

  #revealItem(entries, observer) {
    entries = entries.filter(entry => entry.isIntersecting);
    if (!entries) return;

    entries.forEach(entry => {
      if (entry.target === el.navContainer) {
        el.navContainer.classList.remove('hidden');
        const logo = el.navContainer.querySelector('.logo');
        const navItems = el.navContainer.querySelectorAll('.nav-item');
        logo.classList.add('show-rotate');
        this.#showCollection(navItems, 100, 'pop-from-right');
      }

      if (entry.target.classList.contains('title-dark')) {
        this.#showSingleItem(entry.target, 'pop-title-right');
      }

      if (entry.target === el.me) {
        this.#showSingleItem(entry.target, 'show-rotate-half');
      }

      if (entry.target === el.info) {
        el.info.classList.remove('hidden');
        const infoItemCollection = el.info.querySelectorAll('.info-item');
        el.info.classList.add('pop-info-border');
        this.#showCollection(infoItemCollection, 150, 'pop-info-item');
      }

      if (entry.target === el.progress) {
        el.progress.classList.remove('hidden');
        el.progress.classList.add('pop-progress-bar');
        this.#showCollection(el.percentages, 100, 'show-percentage', false);
      }

      if (entry.target === el.cubeWrapper) {
        this.#showSingleItem(entry.target, 'show-cube');
      }

      if (entry.target === el.description) {
        this.#showSingleItem(entry.target, 'pop-from-bottom');
      }

      if (entry.target === el.works) {
        el.works.classList.remove('hidden');
        this.#showCollection(el.projectCollection, 100, 'pop-from-bottom');
      }

      if (entry.target.classList.contains('card')) {
        this.#showSingleItem(entry.target, 'pop-from-bottom');
      }

      observer.unobserve(entry.target);
    });
  }

  #getPartObserver() {
    return new IntersectionObserver(this.#revealItem.bind(this), {
      threshold: 0.3,
    });
  }

  #getFullObserver() {
    return new IntersectionObserver(this.#revealItem.bind(this), {
      threshold: 1,
    });
  }

  #observeSections() {
    const sectionObserver = new IntersectionObserver(
      function (entries) {
        const entry = entries[0];
        if (entry.isIntersecting) {
          el.navLink.forEach(link => {
            if (!link.classList.contains('resume')) {
              const target = document.querySelector(link.getAttribute('href'));
              if (target === entry.target) {
                link.classList.add('active-link');
              } else {
                link.classList.remove('active-link');
              }
            }
          });
        }
      },
      {
        threshold: 0.1,
      }
    );

    el.navLink.forEach(link => {
      if (!link.classList.contains('resume')) {
        const element = document.querySelector(link.getAttribute('href'));
        sectionObserver.observe(element);
      }
    });
  }

  #observeHeader() {
    const headerObserver = new IntersectionObserver(
      function (entries) {
        const entry = entries[0];
        if (!entry.isIntersecting) {
          el.navbar.classList.add('sticky');
          el.header.style.marginBottom = '4.5rem';
        } else {
          el.navbar.classList.remove('sticky');
          el.header.style.marginBottom = '0';
        }
      },
      {
        threshold: 0,
        rootMargin: `-${el.navbar.getBoundingClientRect().height}px`,
      }
    );

    headerObserver.observe(el.header);
  }

  #observeElements() {
    // get all the observers
    this.#partObserver = this.#getPartObserver();
    this.#fullObserver = this.#getFullObserver();
    let observer;

    if (window.innerWidth > 663) {
      this.#partObserver.observe(el.works);
    } else {
      el.projectCollection.forEach(item => this.#partObserver.observe(item));
    }

    observer =
      window.innerWidth <= 300 || window.innerHeight <= 300
        ? this.#partObserver
        : this.#fullObserver;

    [
      ...el.titleDark,
      el.progress,
      el.cubeWrapper,
      el.me,
      el.info,
      el.description,
    ].forEach(item => observer.observe(item));
    this.#partObserver.observe(el.navContainer);
  }
}

new Application();
